/**
 * AnnotationEditor — full annotation interface for a single task.
 *
 * Layout: toolbar (top) + canvas (center) + class palette (right sidebar)
 */

import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Save, Loader2, Trash2,
  CheckCircle2, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { annotationsApi } from '@/api/endpoints'
import { BBoxCanvas, type BBox } from './BBoxCanvas'
import type { AnnotationClassDef } from '@/types'

interface AnnotationEditorProps {
  projectId: number
  taskId: number
  classSet: AnnotationClassDef[]
  onBack: () => void
  onNavigate: (taskId: number) => void
}

const DEFAULT_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#6366f1', '#a855f7', '#ec4899', '#f43f5e',
]

function getClassColor(classSet: AnnotationClassDef[], classId: number): string {
  const cls = classSet.find(c => c.id === classId)
  return cls?.color ?? DEFAULT_COLORS[classId % DEFAULT_COLORS.length]
}

export function AnnotationEditor({ projectId, taskId, classSet, onBack, onNavigate }: AnnotationEditorProps) {
  const queryClient = useQueryClient()

  // Active class for drawing new boxes
  const [activeClassId, setActiveClassId] = useState(classSet[0]?.id ?? 0)
  const activeClass = classSet.find(c => c.id === activeClassId)

  // Selected box
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null)

  // Local box state (modified by canvas, saved to API)
  const [localBoxes, setLocalBoxes] = useState<BBox[]>([])
  const [isDirty, setIsDirty] = useState(false)

  // Fetch task detail
  const { data: task, isLoading } = useQuery({
    queryKey: ['annotation-task', projectId, taskId],
    queryFn: () => annotationsApi.getTask(projectId, taskId),
    staleTime: 0, // Always fresh when opening editor
  })

  // Fetch neighbors for navigation
  const { data: neighbors } = useQuery({
    queryKey: ['annotation-neighbors', projectId, taskId],
    queryFn: () => annotationsApi.getNeighbors(projectId, taskId),
    staleTime: 30_000,
  })

  // Initialize local boxes from API data
  useEffect(() => {
    if (!task) return
    setLocalBoxes(
      task.annotations.map((a, i) => ({
        id: a.id?.toString() ?? `auto_${i}`,
        class_id: a.class_id,
        class_name: a.class_name,
        x_center: a.x_center,
        y_center: a.y_center,
        width: a.width,
        height: a.height,
        confidence: a.confidence,
        source: a.source,
        color: getClassColor(classSet, a.class_id),
      }))
    )
    setIsDirty(false)
    setSelectedBoxId(null)
  }, [task, classSet])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (status?: string) => annotationsApi.saveAnnotations(projectId, taskId, {
      annotations: localBoxes.map(b => ({
        class_id: b.class_id,
        class_name: b.class_name,
        x_center: b.x_center,
        y_center: b.y_center,
        width: b.width,
        height: b.height,
        confidence: b.confidence,
        source: b.source,
      })),
      status,
    }),
    onSuccess: () => {
      toast.success('Annotations saved')
      setIsDirty(false)
      queryClient.invalidateQueries({ queryKey: ['annotation-tasks', projectId] })
      queryClient.invalidateQueries({ queryKey: ['annotation-project', projectId] })
    },
    onError: () => toast.error('Failed to save'),
  })

  // Handlers
  const handleBoxesChange = useCallback((boxes: BBox[]) => {
    setLocalBoxes(boxes)
    setIsDirty(true)
  }, [])

  const handleSelectBox = useCallback((box: BBox | null) => {
    setSelectedBoxId(box?.id ?? null)
    if (box) setActiveClassId(box.class_id)
  }, [])

  const handleChangeClass = useCallback((classId: number) => {
    setActiveClassId(classId)
    // If a box is selected, change its class
    if (selectedBoxId) {
      const cls = classSet.find(c => c.id === classId)
      if (!cls) return
      setLocalBoxes(prev => prev.map(b =>
        b.id === selectedBoxId
          ? { ...b, class_id: classId, class_name: cls.name, color: cls.color, source: 'corrected' as const }
          : b
      ))
      setIsDirty(true)
    }
  }, [selectedBoxId, classSet])

  const handleDeleteSelected = useCallback(() => {
    if (!selectedBoxId) return
    setLocalBoxes(prev => prev.filter(b => b.id !== selectedBoxId))
    setSelectedBoxId(null)
    setIsDirty(true)
  }, [selectedBoxId])

  const handleSaveAndNext = useCallback(() => {
    saveMutation.mutate('completed', {
      onSuccess: () => {
        if (neighbors?.next_id) {
          onNavigate(neighbors.next_id)
        }
      },
    })
  }, [saveMutation, neighbors, onNavigate])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Number keys 1-9 for class selection
      if (e.key >= '1' && e.key <= '9') {
        const idx = parseInt(e.key) - 1
        if (idx < classSet.length) {
          handleChangeClass(classSet[idx].id)
        }
      }
      // Ctrl+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        saveMutation.mutate(undefined)
      }
      // N for next
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey && neighbors?.next_id) {
        onNavigate(neighbors.next_id)
      }
      // P for previous
      if (e.key === 'p' && !e.ctrlKey && !e.metaKey && neighbors?.prev_id) {
        onNavigate(neighbors.prev_id)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [classSet, handleChangeClass, saveMutation, neighbors, onNavigate])

  if (isLoading || !task) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const selectedBox = localBoxes.find(b => b.id === selectedBoxId)

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>

        <div className="h-4 w-px bg-border" />

        <span className="text-xs text-muted-foreground truncate max-w-[200px]">
          {task.s3_key.split('/').pop()}
        </span>

        <div className="h-4 w-px bg-border" />

        <span className={cn(
          'text-[10px] font-medium px-2 py-0.5 rounded-full',
          task.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' :
          task.status === 'reviewed' ? 'bg-blue-500/10 text-blue-600' :
          'bg-muted text-muted-foreground'
        )}>
          {task.status}
        </span>

        <span className="text-[10px] text-muted-foreground">
          {localBoxes.length} boxes
          {isDirty && ' • unsaved'}
        </span>

        <div className="ml-auto flex items-center gap-1.5">
          {/* Navigation */}
          <Button
            variant="outline" size="sm" className="h-8 w-8 p-0"
            disabled={!neighbors?.prev_id}
            onClick={() => neighbors?.prev_id && onNavigate(neighbors.prev_id)}
            title="Previous (P)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline" size="sm" className="h-8 w-8 p-0"
            disabled={!neighbors?.next_id}
            onClick={() => neighbors?.next_id && onNavigate(neighbors.next_id)}
            title="Next (N)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-border" />

          {/* Save */}
          <Button
            variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            disabled={!isDirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate(undefined)}
            title="Save (Ctrl+S)"
          >
            {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>

          <Button
            size="sm" className="h-8 text-xs gap-1.5"
            disabled={saveMutation.isPending}
            onClick={handleSaveAndNext}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Complete & Next
          </Button>
        </div>
      </div>

      {/* Main area: canvas + class palette */}
      <div className="flex gap-3">
        {/* Canvas */}
        <div className="flex-1 min-w-0">
          <BBoxCanvas
            imageUrl={task.image_url ?? ''}
            boxes={localBoxes}
            activeClassId={activeClassId}
            activeClassName={activeClass?.name ?? ''}
            activeColor={activeClass?.color ?? '#ef4444'}
            onChange={handleBoxesChange}
            onSelect={handleSelectBox}
            selectedBoxId={selectedBoxId}
          />
        </div>

        {/* Right sidebar: class palette + selected box info */}
        <div className="w-48 flex-shrink-0 space-y-3">
          {/* Class palette */}
          <div className="rounded-lg border bg-card p-2 space-y-1">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-1 mb-1">
              Classes (1-9)
            </p>
            <div className="max-h-64 overflow-y-auto space-y-0.5">
              {classSet.map((cls, idx) => (
                <button
                  key={cls.id}
                  className={cn(
                    'w-full flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors text-left',
                    activeClassId === cls.id ? 'bg-primary/10 font-medium' : 'hover:bg-muted'
                  )}
                  onClick={() => handleChangeClass(cls.id)}
                >
                  <span
                    className="h-3 w-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: cls.color }}
                  />
                  <span className="truncate">{cls.name}</span>
                  {idx < 9 && (
                    <span className="ml-auto text-[9px] text-muted-foreground">{idx + 1}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Selected box info */}
          {selectedBox && (
            <div className="rounded-lg border bg-card p-2 space-y-2">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-1">
                Selected
              </p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: selectedBox.color }} />
                  <span className="font-medium">{selectedBox.class_name}</span>
                </div>
                {selectedBox.confidence && (
                  <p className="text-muted-foreground">Confidence: {(selectedBox.confidence * 100).toFixed(1)}%</p>
                )}
                <p className="text-muted-foreground">Source: {selectedBox.source}</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="w-full h-7 text-[10px]"
                onClick={handleDeleteSelected}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Delete Box
              </Button>
            </div>
          )}

          {/* Keyboard shortcuts */}
          <div className="rounded-lg border bg-card p-2">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider px-1 mb-1">
              Shortcuts
            </p>
            <div className="space-y-0.5 text-[10px] text-muted-foreground">
              <p><kbd className="bg-muted px-1 rounded">1-9</kbd> Select class</p>
              <p><kbd className="bg-muted px-1 rounded">Del</kbd> Delete box</p>
              <p><kbd className="bg-muted px-1 rounded">Ctrl+S</kbd> Save</p>
              <p><kbd className="bg-muted px-1 rounded">N</kbd> Next image</p>
              <p><kbd className="bg-muted px-1 rounded">P</kbd> Previous</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
